import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    console.log('[PRICE UPDATE DEBUG] Found new price:', !!newPrice);
    console.log('[PRICE UPDATE DEBUG] Found current price:', !!currentPrice);

    if (!newPrice || !currentPrice) return;

    // Detect the current order on the page (reflects actual toggle state)
    const currentPriceGroup = currentPrice.querySelector('.current-price-group');
    const currentCompareGroup = currentPrice.querySelector('.compare-price-group');
    const currentOrder = this.#detectPriceOrder(currentPrice);
    
    console.log('[PRICE UPDATE DEBUG] Current order detected:', currentOrder);

    // Extract new price values from server response
    const newPriceValue = newPrice.querySelector('.current-price-group .price')?.innerHTML;
    const newCompareValue = newPrice.querySelector('.compare-price-group .compare-at-price')?.innerHTML;
    const newSavingsBadge = newPrice.querySelector('.savings-badge')?.outerHTML;
    const newStarRating = newPrice.querySelector('.price-star-rating')?.outerHTML;

    // Update only the VALUES, keeping the current order
    if (currentPriceGroup) {
      const priceSpan = currentPriceGroup.querySelector('.price');
      if (priceSpan && newPriceValue) {
        priceSpan.innerHTML = newPriceValue;
      }
    }

    if (currentCompareGroup && newCompareValue) {
      const compareSpan = currentCompareGroup.querySelector('.compare-at-price');
      if (compareSpan) {
        compareSpan.innerHTML = newCompareValue;
      }
    }

    // Update or add savings badge
    const currentSavingsBadge = currentPrice.querySelector('.savings-badge');
    if (newSavingsBadge) {
      if (currentSavingsBadge) {
        currentSavingsBadge.outerHTML = newSavingsBadge;
      } else {
        currentPrice.insertAdjacentHTML('beforeend', newSavingsBadge);
      }
    } else if (currentSavingsBadge) {
      currentSavingsBadge.remove();
    }

    // Update or add star rating
    const currentStarRating = currentPrice.querySelector('.price-star-rating');
    if (newStarRating) {
      if (currentStarRating) {
        currentStarRating.outerHTML = newStarRating;
      } else {
        currentPrice.insertAdjacentHTML('beforeend', newStarRating);
      }
    } else if (currentStarRating) {
      currentStarRating.remove();
    }

    console.log('[PRICE UPDATE DEBUG] Updated price values while preserving order');
  };

  #detectPriceOrder(priceContainer) {
    const currentPriceGroup = priceContainer.querySelector('.current-price-group');
    const comparePriceGroup = priceContainer.querySelector('.compare-price-group');

    if (currentPriceGroup && comparePriceGroup) {
      const children = Array.from(priceContainer.children);
      const currentIndex = children.indexOf(currentPriceGroup);
      const compareIndex = children.indexOf(comparePriceGroup);
      
      return compareIndex < currentIndex ? 'strikethrough-first' : 'sale-first';
    }
    
    return 'unknown';
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
